/**
 * The steps of an export and the slice of the job's progress (0..1) each one
 * reports in. The worker reports progress inside these bands and the app reads
 * them back to say what the export is doing, so the two stay in step by sharing
 * this list. (Previews, stills and split exports report their own ranges.)
 */
export const RENDER_STAGES = [
  { id: 'prepare', label: 'Preparing', from: 0, to: 0.04 },
  { id: 'fetch', label: 'Fetching media', from: 0.04, to: 0.25 },
  { id: 'render', label: 'Rendering', from: 0.25, to: 0.93 },
  { id: 'upload', label: 'Uploading', from: 0.93, to: 1 },
] as const;

export type RenderStageId = (typeof RENDER_STAGES)[number]['id'];

/** Progress for `fraction` (0..1) of the way through a stage. */
export function stageProgress(stage: RenderStageId, fraction: number): number {
  const s = RENDER_STAGES.find((x) => x.id === stage)!;
  return s.from + (s.to - s.from) * Math.min(1, Math.max(0, fraction));
}

/** The step a running export is in, and how far through it (0..1). */
export function renderStageOf(progress: number): { index: number; stage: (typeof RENDER_STAGES)[number]; fraction: number } {
  const p = Math.min(1, Math.max(0, progress));
  const index = Math.max(0, RENDER_STAGES.findIndex((s) => p < s.to));
  const stage = RENDER_STAGES[p >= 1 ? RENDER_STAGES.length - 1 : index];
  return { index: RENDER_STAGES.indexOf(stage), stage, fraction: Math.min(1, (p - stage.from) / (stage.to - stage.from)) };
}
